/**
 * planPdf — generación NATIVA del PDF del plan (sin `window.print()`).
 *
 * Estrategia:
 *   - Cada SECCIÓN del plan (cover, client, routine, diet, etc.) se captura
 *     por separado y arranca en una hoja PDF nueva. No se mezclan secciones
 *     en una misma hoja, así "Rutina" no se corta con "Dieta".
 *   - Si una sección excede el alto de A4, se corta INTERNAMENTE en los
 *     bordes de bloques atómicos (días, comidas, filas de tabla, sets de
 *     ejercicio…) → un día de la rutina nunca se parte por la mitad.
 *   - Las imágenes se "pre-cargan" antes de la captura (eager) para que los
 *     GIFs aparezcan correctamente en el PDF — `loading="lazy"` se quita en
 *     la copia que html2canvas snapshotea (sin tocar el DOM en vivo).
 *   - Los GIFs envueltos en `<a href="/exercise/...">` quedan CLICKEABLES en
 *     el PDF — al tocarlos abren la página pública del ejercicio.
 *   - La marca de agua se dibuja CENTRADA por hoja vía jsPDF (no depende del
 *     DOM): garantía de aparecer centrada en todas las hojas.
 *
 * jspdf + html2canvas se importan dinámicamente para no engordar el bundle
 * inicial — solo se cargan cuando el coach exporta.
 */

interface ExportPlanPdfOptions {
  /** Elemento raíz a rasterizar — el `.al-print-root` montado por PrintablePlanView. */
  root: HTMLElement;
  /** Nombre del archivo (sin extensión). */
  fileName: string;
  /** URL del logo para marca de agua centrada. Null = sin marca de agua. */
  watermarkUrl?: string | null;
  /** Plan en tema oscuro → fondo de página oscuro. */
  dark?: boolean;
}

/**
 * Cortes de página en TRES niveles, por prioridad:
 *
 *  Nivel 1 — FORZADO: los resúmenes semanales (rutina / dieta) son hojas
 *    imprimibles aparte. SIEMPRE empiezan y terminan en una hoja propia.
 *  Nivel 2 — PREFERIDO: bloques atómicos del editor (día completo, comida,
 *    tabla, sesión de cardio…). Si caben enteros, se mantienen juntos.
 *  Nivel 3 — FALLBACK: filas de tabla / párrafos. Solo se usa cuando ni
 *    siquiera un bloque preferido (ej. un día con muchos ejercicios) cabe
 *    en una hoja sola.
 */
const FORCE_SEL = ".al-routine-wk, .al-diet-wk";
const PREFERRED_SEL = [
  ".al-day",
  ".al-diet-meal",
  ".al-diet-day",
  ".al-bio-grid",
  ".al-supp-row",
  ".al-cardio-session",
  ".al-pg-header",
  ".al-pg-table",
  "h1, h2, h3",
].join(",");
const FALLBACK_SEL = ".al-pg-table tbody tr, p, li, .al-macro";

interface Breakpoints {
  force: number[];     // tops + bottoms de los bloques forzados
  preferred: number[];
  fallback: number[];
}

/** Carga una imagen respetando CORS — para la marca de agua dibujada por jsPDF. */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/** Extrae la(s) URL(s) de un valor `background-image` CSS (`url("…")`). */
function extractCssUrls(bg: string): string[] {
  if (!bg || bg === "none") return [];
  const out: string[] = [];
  const re = /url\((['"]?)([^'")]+)\1\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(bg)) !== null) {
    const u = m[2];
    if (u && !u.startsWith("data:")) out.push(u);
  }
  return out;
}

/**
 * Pre-carga todas las imágenes del root con `crossOrigin="anonymous"` ANTES de
 * llamar a html2canvas. Esto rellena la cache del navegador con la versión
 * CORS-clean de cada GIF/foto, así cuando html2canvas las pide (con useCORS),
 * el browser las sirve sin reload y sin contaminar el canvas. Sin este paso,
 * los GIFs a veces salían en blanco en el PDF aunque el server mande CORS.
 *
 * IMPORTANTE: incluye también las imágenes declaradas como `background-image`
 * (la FOTO DE PORTADA es un `<div style="background-image:url(...)">`, no un
 * `<img>`) — antes no se precargaban y salían en blanco en el PDF.
 *
 * Se usa `img.decode()` para garantizar que el bitmap esté DECODIFICADO (no
 * solo descargado) antes de capturar — si solo esperamos `onload`, html2canvas
 * a veces dibuja el frame antes de que el browser termine de decodificar.
 */
async function prewarmImages(root: HTMLElement, timeoutMs = 20000): Promise<void> {
  const srcs = new Set<string>();
  // <img> directos.
  root.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
    if (img.src) srcs.add(img.src);
  });
  // background-image (portada + cualquier deco). Resolvemos a URL absoluta
  // contra el documento para que coincida con lo que pedirá html2canvas.
  root.querySelectorAll<HTMLElement>("*").forEach((el) => {
    const bg = getComputedStyle(el).backgroundImage;
    for (const raw of extractCssUrls(bg)) {
      try {
        srcs.add(new URL(raw, document.baseURI).href);
      } catch {
        srcs.add(raw);
      }
    }
  });
  if (srcs.size === 0) return;
  const loads = Array.from(srcs).map(
    (src) =>
      new Promise<void>((resolve) => {
        const i = new Image();
        i.crossOrigin = "anonymous";
        const done = () => resolve();
        i.onload = () => {
          // decode() puede rechazar en algunos browsers — no es fatal.
          if (typeof i.decode === "function") i.decode().then(done, done);
          else done();
        };
        i.onerror = done;
        i.src = src;
      }),
  );
  await Promise.race([
    Promise.all(loads).then(() => undefined),
    new Promise<void>((r) => setTimeout(r, timeoutMs)),
  ]);
}

/** Recolecta los 3 niveles de cortes (en CSS px relativos al `sectionEl`). */
function collectBreakpoints(sectionEl: HTMLElement): Breakpoints {
  const baseTop = sectionEl.getBoundingClientRect().top;
  const force = new Set<number>();
  const preferred = new Set<number>();
  const fallback = new Set<number>();

  // FORZADO: top y bottom de cada resumen semanal — para que la hoja
  // empiece fresca con el resumen y termine fresca después.
  sectionEl.querySelectorAll<HTMLElement>(FORCE_SEL).forEach((el) => {
    const r = el.getBoundingClientRect();
    const top = Math.round(r.top - baseTop);
    const bot = Math.round(r.bottom - baseTop);
    if (top > 0) force.add(top);
    if (bot > 0) force.add(bot);
  });
  sectionEl.querySelectorAll<HTMLElement>(PREFERRED_SEL).forEach((el) => {
    const top = Math.round(el.getBoundingClientRect().top - baseTop);
    if (top > 0) preferred.add(top);
  });
  sectionEl.querySelectorAll<HTMLElement>(FALLBACK_SEL).forEach((el) => {
    const top = Math.round(el.getBoundingClientRect().top - baseTop);
    if (top > 0) fallback.add(top);
  });

  return {
    force: Array.from(force).sort((a, b) => a - b),
    preferred: Array.from(preferred).sort((a, b) => a - b),
    fallback: Array.from(fallback).sort((a, b) => a - b),
  };
}

/**
 * Elige dónde cortar la hoja actual dado el rango disponible. Regla:
 *  1. Si hay un corte FORZADO en (start, limit], usar el PRIMERO (cortar antes).
 *  2. Si no, usar el último corte PREFERIDO en el rango.
 *  3. Si no, usar el último corte FALLBACK.
 *  4. Si no hay nada, corte duro en `limit`.
 */
function pickBreakpoint(
  bp: Breakpoints,
  startCss: number,
  limitCss: number,
  totalCss: number,
): number {
  const forceHit = bp.force.find((v) => v > startCss && v <= limitCss);
  if (forceHit !== undefined) return forceHit;
  let lastPref = 0;
  for (const v of bp.preferred) if (v > startCss && v <= limitCss) lastPref = v;
  if (lastPref > 0) return lastPref;
  let lastFall = 0;
  for (const v of bp.fallback) if (v > startCss && v <= limitCss) lastFall = v;
  if (lastFall > 0) return lastFall;
  return Math.min(limitCss, totalCss);
}

/** Convierte los `<a href><img></a>` visibles de una sección en hyperlinks
 *  jsPDF, ubicados a la coordenada correcta DENTRO de la franja [startCss, endCss]
 *  de la sección. Si el enlace queda fuera de la franja, se ignora. */
function addLinksForSlice(
  pdf: { link: (x: number, y: number, w: number, h: number, opts: { url: string }) => void },
  sectionEl: HTMLElement,
  startCss: number,
  endCss: number,
  ptPerCss: number,
): void {
  const baseRect = sectionEl.getBoundingClientRect();
  const anchors = sectionEl.querySelectorAll<HTMLAnchorElement>("a[href]");
  anchors.forEach((a) => {
    const href = a.href;
    if (!href) return;
    const rect = a.getBoundingClientRect();
    const topCss = rect.top - baseRect.top;
    const bottomCss = rect.bottom - baseRect.top;
    if (bottomCss <= startCss || topCss >= endCss) return;
    const visibleTop = Math.max(topCss, startCss) - startCss;
    const visibleBottom = Math.min(bottomCss, endCss) - startCss;
    const x = (rect.left - baseRect.left) * ptPerCss;
    const y = visibleTop * ptPerCss;
    const w = rect.width * ptPerCss;
    const h = (visibleBottom - visibleTop) * ptPerCss;
    if (w <= 0 || h <= 0) return;
    pdf.link(x, y, w, h, { url: href });
  });
}

export async function exportPlanPdf({
  root,
  fileName,
  watermarkUrl,
  dark,
}: ExportPlanPdfOptions): Promise<void> {
  const [{ default: html2canvas }, jsPdfMod] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);
  const JsPDF = jsPdfMod.jsPDF;

  const bg = dark ? "#0b0b0b" : "#ffffff";
  const sections = Array.from(root.querySelectorAll<HTMLElement>(".al-print-page"));
  if (sections.length === 0) return;

  // Esperar a que TODOS los GIFs / fotos del plan estén en cache CORS-clean
  // antes de capturar — si no, salen en blanco en el PDF.
  await prewarmImages(root);
  // Esperar a que las fuentes web terminen de cargar — si no, html2canvas
  // captura con la fuente fallback y el layout queda más alto/ancho de lo
  // real (texto que "se corre" y tablas que se ven cortadas).
  try {
    if ((document as any).fonts?.ready) await (document as any).fonts.ready;
  } catch {
    /* no-op */
  }

  const pdf = new JsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageWpt = pdf.internal.pageSize.getWidth();
  const pageHpt = pdf.internal.pageSize.getHeight();

  // Cargar logo para marca de agua (best-effort — si CORS falla, seguimos sin ella).
  let wm: HTMLImageElement | null = null;
  if (watermarkUrl) {
    try {
      wm = await loadImage(watermarkUrl);
    } catch {
      wm = null;
    }
  }

  const drawWatermark = () => {
    if (!wm || !wm.naturalWidth) return;
    const w = pageWpt * 0.55;
    const h = (wm.naturalHeight / wm.naturalWidth) * w;
    const x = (pageWpt - w) / 2;
    const y = (pageHpt - h) / 2;
    const gs = (pdf as any).GState ? (pdf as any).GState({ opacity: 0.1 }) : null;
    if (gs) pdf.setGState(gs);
    try {
      pdf.addImage(wm, "PNG", x, y, w, h);
    } catch {
      /* imagen tainted — ignorar */
    }
    if (gs) pdf.setGState((pdf as any).GState({ opacity: 1 }));
  };

  let firstPage = true;

  // Tope seguro de dimensión de canvas. Chrome corta a 16384 px por lado (y el
  // ÁREA total también está limitada); Safari/Firefox son aún más bajos. Si una
  // sección a scale=2 supera esto, html2canvas devuelve zonas EN BLANCO → eso
  // causaba hojas vacías y tablas "cortadas" en planes largos (la rutina). Por
  // eso bajamos el scale solo lo necesario para que quepa, con piso 1 para no
  // perder demasiada nitidez del texto.
  const MAX_CANVAS_PX = 14000;

  for (const sectionEl of sections) {
    const sectionWidthPx = sectionEl.offsetWidth || 820;
    const sectionHeightPx = sectionEl.scrollHeight || sectionEl.offsetHeight || 0;

    // scale=2 por defecto; se reduce si la sección es tan alta/ancha que el
    // canvas resultante reventaría el límite del navegador.
    const longestPx = Math.max(sectionWidthPx, sectionHeightPx) || sectionWidthPx;
    const scale = Math.max(1, Math.min(2, MAX_CANVAS_PX / longestPx));

    // Capturar ESTA sección
    const canvas = await html2canvas(sectionEl, {
      scale,
      useCORS: true,
      allowTaint: false,
      imageTimeout: 25000,
      backgroundColor: bg,
      logging: false,
      windowWidth: sectionWidthPx,
      // No incluir la marca de agua del DOM (la dibujamos centrada por hoja).
      ignoreElements: (el) => el.classList?.contains("al-pg-watermark"),
      onclone: (clonedDoc: Document) => {
        // Forzar carga eager + crossOrigin en la COPIA (no toca el editor en vivo).
        clonedDoc.querySelectorAll("img").forEach((img) => {
          img.removeAttribute("loading");
          if (!img.crossOrigin) img.crossOrigin = "anonymous";
        });
      },
    });

    const pxPerCss = canvas.width / sectionWidthPx;
    const ptPerCss = pageWpt / sectionWidthPx;
    // Altura (en CSS px) que cabe en una hoja A4 a ancho completo.
    const pageCssPx = pageHpt / ptPerCss;
    // Usar la altura REAL del canvas (no offsetHeight), por si hay diferencias.
    const sectionCssH = canvas.height / pxPerCss;

    const bp = sectionCssH > pageCssPx + 4
      ? collectBreakpoints(sectionEl)
      : { force: [], preferred: [], fallback: [] };

    let startCss = 0;
    let guard = 0;
    while (startCss < sectionCssH - 1 && guard < 200) {
      guard++;
      const limitCss = startCss + pageCssPx;
      let endCss: number;
      if (sectionCssH <= pageCssPx + 4) {
        // La sección entera entra en una hoja
        endCss = sectionCssH;
      } else {
        endCss = pickBreakpoint(bp, startCss, limitCss, sectionCssH);
        // Si lo que queda es casi nada, completar
        if (sectionCssH - endCss < 4) endCss = sectionCssH;
        // Garantía mínima de progreso para evitar loops infinitos
        if (endCss <= startCss) endCss = Math.min(limitCss, sectionCssH);
      }

      const sliceCssH = endCss - startCss;
      const srcY = Math.round(startCss * pxPerCss);
      const srcH = Math.round(sliceCssH * pxPerCss);

      const slice = document.createElement("canvas");
      slice.width = canvas.width;
      slice.height = srcH;
      const ctx = slice.getContext("2d")!;
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, slice.width, slice.height);
      ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);

      if (!firstPage) pdf.addPage();
      firstPage = false;

      pdf.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, pageWpt, sliceCssH * ptPerCss);
      drawWatermark();

      // Hyperlinks (GIFs envueltos en <a>) → clickeables en el PDF.
      addLinksForSlice(pdf, sectionEl, startCss, endCss, ptPerCss);

      startCss = endCss;
    }
  }

  pdf.save(`${fileName}.pdf`);
}
